import type { GameEvent } from "../shared/protocol.js";

// Bombermaaan's sound effects and match music (public/sfx, GPL-3).
const SOUNDS: Partial<Record<GameEvent, string[][]>> = {
  drop: [["bomb_drop"]],
  bounce: [["bomb_bounce"]],
  throw: [["bomber_throw"]],
  punch: [["bomber_punch"]],
  pick: [["pick_item_1"], ["pick_item_2"]],
  sick: [["sick_1"], ["sick_2"]],
  contaminate: [["sick_3"]],
  lose: [["bomber_lose_item"]],
  death: [["bomber_death"]],
  fumes: [["item_fumes"]],
  hurry: [["hurry"]],
  clap: [["wall_clap_1"], ["wall_clap_2"]],
  ding: [["ring_ding"]],
  winner: [["winner"]],
  draw: [["draw_game", "draw_game_voice"]],
  victory: [["victory", "victory_voice"]],
};
for (let n = 1; n <= 10; n++) {
  const nn = String(n).padStart(2, "0");
  SOUNDS[`boom${n}` as GameEvent] = [[`explosion_${nn}_1`], [`explosion_${nn}_2`]];
}

const VOLUME = 0.5; // Bombermaaan plays samples at half volume
const MUSIC_VOLUME = 0.35;

export class Sfx {
  private ctx: AudioContext | null = null;
  private buffers = new Map<string, Promise<AudioBuffer | null>>();
  private music: HTMLAudioElement | null = null;
  private wantMusic = false;
  muted = false;

  constructor() {
    try {
      this.muted = localStorage.getItem("bman:muted") === "1";
    } catch {}
  }

  toggle() {
    this.muted = !this.muted;
    try {
      localStorage.setItem("bman:muted", this.muted ? "1" : "0");
    } catch {}
    this.syncMusic();
    return this.muted;
  }

  // Browsers only allow audio after a user gesture.
  unlock() {
    if (!this.ctx) {
      this.ctx = new AudioContext();
      for (const set of Object.values(SOUNDS)) for (const names of set!) names.forEach((n) => this.load(n));
    }
    if (this.ctx.state === "suspended") void this.ctx.resume();
    this.syncMusic();
  }

  private load(name: string): Promise<AudioBuffer | null> {
    let p = this.buffers.get(name);
    if (!p) {
      p = fetch(`/sfx/${name}.mp3`)
        .then((r) => r.arrayBuffer())
        .then((data) => this.ctx!.decodeAudioData(data))
        .catch(() => null);
      this.buffers.set(name, p);
    }
    return p;
  }

  play(e: GameEvent) {
    const options = SOUNDS[e];
    if (!options || this.muted || !this.ctx || this.ctx.state !== "running") return;
    const names = options[Math.floor(Math.random() * options.length)];
    for (const name of names) {
      void this.load(name).then((buf) => {
        if (!buf || !this.ctx) return;
        const src = this.ctx.createBufferSource();
        src.buffer = buf;
        const gain = this.ctx.createGain();
        gain.gain.value = VOLUME;
        src.connect(gain).connect(this.ctx.destination);
        src.start();
      });
    }
  }

  // Match music loops while a round is being played.
  setMusic(on: boolean) {
    if (on === this.wantMusic) return;
    this.wantMusic = on;
    if (on && this.music) this.music.currentTime = 0;
    this.syncMusic();
  }

  private syncMusic() {
    if (!this.ctx) return; // not before the first user gesture
    if (!this.music) {
      this.music = new Audio("/sfx/match_music.mp3");
      this.music.loop = true;
      this.music.volume = MUSIC_VOLUME;
    }
    if (this.wantMusic && !this.muted) void this.music.play().catch(() => {});
    else this.music.pause();
  }
}
